import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useCustomerAnalytics } from "../../../services/hooks/useCustomerAnalytics";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../services/hooks/useTranslationI18";
import { setSelectedLanguageAndCurrency } from "../../../services/redux/slices/configSlice";
import LS from "../../../utils/LocalStorage";
import {
  CUSTOMER_ANALYTICS_EVENT,
  LocalStorageEnum,
} from "../../../utils/enums";
import { handleRedirectToHomepagePartnerApp } from "../../../utils/help/navigation/navigation";
import { PreferredCountryEnum } from "../../../utils/types";
import { BackArrowIcon } from "../Icons/Icons";
import styles from "./LangSwitch.module.scss";

export const LangSwitch = (props: {
  preferredCountry?: string;
  withMargin?: boolean;
  /** Fired with the newly-selected language flag when the user toggles language. */
  onToggle?: (lang: string) => void;
}) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const addCustomerAnalytics = useCustomerAnalytics();

  const { t, i18n } = useTranslationI18();
  const [langs, setLangs] = useState<{ flag: string; langName: string }[]>([]);

  const language = useAppSelector((store) => store.config.selectedLanguage);
  const preferredCountry = useAppSelector(
    (store) => store.config.restaurantInfo?.preferredCountry
  );

  const secondLanguage = props.preferredCountry || preferredCountry;

  const displayPartnersBackButton =
    LS._get(LocalStorageEnum.MOBILE_APP_PARTNER) ||
    LS._get(LocalStorageEnum.HAS_BACK_BUTTON);

  const organizationCustomer = useAppSelector(
    (state) => state.partnerAccount.organizationCustomer
  );

  useEffect(() => {
    setLangs(getLanguagesFromCountry(secondLanguage as PreferredCountryEnum));
  }, [preferredCountry]);

  const handleRoute = (locale: any) => {
    router.push(router.asPath, router.asPath, { locale: locale });
  };

  const toggleLang = (e: any) => {
    LS._set(LocalStorageEnum.LANG, e);
    const currency =
      preferredCountry === PreferredCountryEnum.EG
        ? ` ${t("common:currency-egp")} `
        : preferredCountry === PreferredCountryEnum.SA
        ? ` ${t("common:currency-sar")} `
        : preferredCountry === PreferredCountryEnum.UAE
        ? ` ${t("common:currency-aed")} `
        : preferredCountry === PreferredCountryEnum.KW
        ? ` ${t("common:currency-kwd")} `
        : preferredCountry === PreferredCountryEnum.BH
        ? ` ${t("common:currency-bhd")} `
        : "$";

    dispatch(setSelectedLanguageAndCurrency({ currency, selectedLanguage: e }));

    addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.CHANGE_LANGUAGE, {
      chosenLang: e,
    });

    props.onToggle?.(e);

    handleRoute(e);
    setTimeout(() => {
      document.body.dir = i18n?.dir(e);
    }, 0);
  };

  const PartnerBackButton = () => {
    return displayPartnersBackButton ? (
      <button
        data-testid={"partner-back"}
        onClick={() => handleRedirectToHomepagePartnerApp(organizationCustomer)}
        className={styles.back_button}
      >
        <BackArrowIcon />
        Back
      </button>
    ) : (
      <></>
    );
  };

  return (
    <div className={styles.lang_buttons}>
      <PartnerBackButton />
      <div
        className={`${styles.btns_wrapper} ${
          props.withMargin ? styles.margin_inline : ""
        }`}
      >
        {language &&
          langs.map((lang: any, i: number) => {
            return (
              <button
                key={i}
                data-testid={"lang-switch-" + lang.flag}
                aria-label={lang.langName}
                onClick={() => toggleLang(lang.flag)}
                className={`${
                  (language === lang.flag && language !== undefined
                    ? styles.active
                    : "") +
                  " " +
                  styles.lang_btn
                } ${
                  lang.flag === "ar-SA" && lang.langName === "Arabic"
                    ? styles.ar_sa_lang_btn
                    : ""
                }`}
              >
                {t(`common:${lang.langName}`)}
              </button>
            );
          })}
      </div>
    </div>
  );
};

export const getLanguagesFromCountry = (country: PreferredCountryEnum) => {
  return [
    {
      flag: "en",
      langName: "English",
    },

    country === PreferredCountryEnum.CA
      ? {
          flag: "fr",
          langName: "French",
        }
      : {
          flag:
            country === PreferredCountryEnum.SA ||
            country === PreferredCountryEnum.KW ||
            country === PreferredCountryEnum.BH
              ? "ar-SA"
              : "ar",
          langName: "Arabic",
        },
  ];
};
